import type { CallOff } from '../types';

import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodCallOffResponseSchema } from './validations';

/**
 * Fetches a single call-off by ID from the API with all line items.
 *
 * @param callOffId - The UUID of the call-off to fetch.
 * @param options - Additional request options.
 * @returns The call-off data including all line items.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Off/operation/get_calloff_calloffs__calloff_id__get
 */
export const fetchCallOff = async (
  callOffId: UUID,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.CALL_OFF.GET(callOffId), {
      ...options,
    })
    .json<CallOff | undefined>();

  // Validate the response with Zod
  const validatedResponse = zodCallOffResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'CallOff API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
